<template>
    <div>
        <app-layout>
            <template v-slot:header>
                <Header
                    :title="$t('reimbursement_forms')"
                    :isColumns="true"
                    @filter-div-status="datatable.filterStatus = $event"
                >
                    <template v-slot:columns>
                        <div
                            class="mb-1"
                            v-for="(column, key) in datatable.columns"
                            :key="key"
                        >
                            <b-form-checkbox
                                :id="'checkbox-' + key"
                                :name="'checkbox-' + key"
                                :value="false"
                                :unchecked-value="true"
                                v-model="column.hidden"
                                v-if="column.field != 'buttons'"
                            >
                                {{ column.label }}
                            </b-form-checkbox>
                        </div>
                    </template>
                </Header>
            </template>
            <template v-slot:header-mobile>
                <HeaderMobile
                    :title="$t('reimbursement_forms')"
                    :isColumns="true"
                    @filter-div-status="datatable.filterStatus = $event"
                >
                    <template v-slot:columns>
                        <div
                            class="mb-1"
                            v-for="(column, key) in datatable.columns"
                            :key="key"
                        >
                            <b-form-checkbox
                                :id="'checkbox-' + key"
                                :name="'checkbox-' + key"
                                :value="false"
                                :unchecked-value="true"
                                v-model="column.hidden"
                                v-if="column.field != 'buttons'"
                            >
                                {{ column.label }}
                            </b-form-checkbox>
                        </div>
                    </template>
                </HeaderMobile>
            </template>
            <datatable-filter
                v-show="datatable.filterStatus"
                @filter="filter"
                @filterClear="filterClear"
                :export-excel="checkPermission('reimbursementform_excelexport')"
                @exportExcel="exportExcel"
            >
                <b-row>
                    <b-col cols="12" md="4">
                        <b-form-group :label="$t('status')">
                            <parameter-selectbox
                                code="reimbursement_form_statuses"
                                v-model="datatable.queryParams.filter.status"
                            ></parameter-selectbox>
                        </b-form-group>
                    </b-col>
                </b-row>
            </datatable-filter>
            <datatable
                :isLoading.sync="datatable.isLoading"
                :columns="datatable.columns"
                :rows="datatable.rows"
                :total="datatable.total"
                :queryParams="datatable.queryParams"
                @on-page-change="onPageChange"
                @on-sort-change="onSortChange"
                @on-per-page-change="onPerPageChange"
                v-show="datatable.showTable"
            >
            </datatable>
        </app-layout>
    </div>
</template>

<script>
// Template
import AppLayout from "@/layouts/AppLayout";
import Header from "@/layouts/AppLayout/Header";
import HeaderMobile from "@/layouts/AppLayout/HeaderMobile";

// Components
import DatatableFilter from "@/components/datatable/DatatableFilter";
import Datatable from "@/components/datatable/Datatable";

// Services
import ReimbursementFormService from "@/services/ReimbursementFormService";

// Others
import qs from "qs";

export default {
    name: "ReimbursementIndex",
    components: {
        AppLayout,
        Header,
        HeaderMobile,
        DatatableFilter,
        Datatable,
    },
    metaInfo() {
        return {
            title: this.$t("reimbursement_forms"),
        };
    },
    data() {
        return {
            datatable: {
                isLoading: false,
                columns: [
                    {
                        label: "",
                        field: "buttons",
                        html: true,
                        sortable: false,
                        tdClass: "p-0 text-center w-40 align-middle",
                        buttons: [
                            {
                                text: this.$t("detail"),
                                class: "ri-edit-box-line",
                                permission: 'reimbursementform_show',
                                callback: (row) => {
                                    this.$router.push('/reimbursement/' + row.id)
                                },
                            },
                            {
                                text: this.$t("approval_steps_report"),
                                class: "ri-file-pdf-line text-danger",
                                permission: 'reimbursementform_approvalstepsreport',
                                callback: (row) => {
                                    this.downloadApprovalStepsReport(row.id);
                                },
                            },
                        ],
                    },
                    {
                        label: this.toUpperCase("id"),
                        field: "id",
                        hidden: true,
                    },
                    {
                        label: this.toUpperCase("requester"),
                        field: "full_name",
                        formatFn: (value, row) => row.full_name || `${row.name || ''} ${row.surname || ''}`.trim() || '-',
                        hidden: false,
                    },
                    {
                        label: this.toUpperCase("employee_id"),
                        field: "employee_id",
                        hidden: false,
                    },
                    {
                        label: this.toUpperCase("job_title"),
                        field: "job_title_name",
                        hidden: false,
                    },
                    {
                        label: this.toUpperCase("budgetary_department"),
                        field: "budgetary_department_name",
                        hidden: false,
                    },
                    {
                        label: this.toUpperCase("total_amount"),
                        field: "grand_total",
                        formatFn: (value) => this.currencyFormat(value),
                        hidden: false,
                    },
                    {
                        label: this.toUpperCase("date_submitted"),
                        field: "submitted_at",
                        hidden: false,
                    },
                    {
                        label: this.toUpperCase("status"),
                        field: "status_name",
                        hidden: false,
                    },
                    {
                        label: this.toUpperCase("approval_stage"),
                        field: "active_step",
                        formatFn: (value, row) => row.active_step ? row.active_step.name : '-',
                        hidden: false,
                    },
                    {
                        label: this.toUpperCase("date_approval_sent"),
                        field: "last_approve_date",
                        hidden: false,
                    },
                ],
                rows: [],
                total: 0,
                filterStatus: true,
                showTable: false,
                queryParams: {
                    filter: {},
                    sort: "-created_at",
                    page: 1,
                    limit: 20,
                },
            },
        };
    },
    created() {
        this.filterSet();
        this.getRows();
    },
    methods: {
        filterSet() {
            this.datatable.queryParams.filter = {};
        },
        filterClear() {
            this.filterSet();
            this.getRows();
        },
        filter() {
            this.datatable.queryParams.page = 1;
            this.getRows();
        },
        onPageChange(params) {
            this.datatable.queryParams.page = params.currentPage;
            this.getRows();
        },
        onPerPageChange(params) {
            this.datatable.queryParams.limit = params.currentPerPage;
            this.datatable.queryParams.page = 1;
            this.getRows();
        },
        onSortChange(params) {
            const sortType = params[0].type == "desc" ? "-" : "";
            this.datatable.queryParams.sort = sortType + params[0].field;
            this.getRows();
        },
        getRows() {
            this.datatable.showTable = true;
            this.datatable.isLoading = true;

            const config = {
                params: {
                    ...this.datatable.queryParams,
                },
                paramsSerializer: (params) =>
                    qs.stringify(params, { encode: false }),
            };

            return ReimbursementFormService.getAll(config)
                .then((response) => {
                    this.datatable.rows = response.data.data || [];
                    this.datatable.total = response.data.pagination.total;
                })
                .finally(() => {
                    this.datatable.isLoading = false;
                });
        },
        exportExcel() {
            const config = {
                params: {
                    ...this.datatable.queryParams,
                },
                paramsSerializer: (params) =>
                    qs.stringify(params, { encode: false }),
            };

            ReimbursementFormService.excelExport(config)
                .then((response) => {
                    this._downloadFile(response, `${this.$t('reimbursement_forms')}.xlsx`);
                })
                .catch((error) => {
                    this.showErrors(error);
                });
        },
        downloadApprovalStepsReport(id) {
            ReimbursementFormService.getApprovalStepsReport(id)
                .then((response) => {
                    this._downloadFile(response, `approval-steps-report-${id}.pdf`);
                })
                .catch((error) => {
                    this.showErrors(error);
                });
        },
    },
};
</script>
